import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { TITLES, type Route } from '../../app/nav'
import { useChrome } from '../../app/theme'
import { api, type OutboundStatus } from '../../lib/api'
import { useSession } from '../../lib/session'
import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { IconButton } from '../ui/button'

/**
 * The top bar.
 *
 * 52px, `panel`, one `line` border underneath — the same height as the sidebar's brand block, so
 * the two hairlines meet and read as one line across the seam. Padding is the design's:
 * `0 12px 0 10px`, so the 30px controls sit on the same rail as the content below.
 *
 * It carries what the design puts here: the workspace, where you are, a global search, background
 * jobs, notifications, help, and the two display switches.
 */
export function TopBar({
  route,
  crumb,
  onOpenNav,
  onOpenPalette,
}: {
  route: Route
  crumb?: string
  onOpenNav: () => void
  onOpenPalette: () => void
}) {
  const { user } = useSession()
  const { theme, density, toggleTheme, toggleDensity } = useChrome()
  const [menu, setMenu] = useState<'workspace' | 'notifications' | 'help' | 'user' | null>(null)

  const outbound = useQuery({
    queryKey: ['outbound'],
    queryFn: () => api.get<OutboundStatus>('/system/outbound'),
    refetchInterval: 15_000,
  })

  const known = outbound.isSuccess
  const sending = outbound.data?.enabled ?? false
  const stoppedByPlatform = outbound.data?.platform === false

  return (
    <header className="bg-panel border-line relative z-30 flex h-[52px] shrink-0 items-center gap-2 border-b pr-3 pl-2.5">
      <IconButton
        icon="panelLeft"
        label="Open navigation"
        variant="quiet"
        onClick={onOpenNav}
        className="lg:hidden"
      />

      {/* The workspace. Since P027 which organization you are acting in is a real question, so it
          is the first thing in the bar rather than a footnote. */}
      <Menu
        open={menu === 'workspace'}
        onToggle={() => setMenu(menu === 'workspace' ? null : 'workspace')}
        trigger={
          <span className="flex items-center gap-2">
            <span className="bg-accent h-[18px] w-[18px] shrink-0 rounded-[5px]" aria-hidden />
            <span className="max-w-[22ch] truncate text-[12.5px] font-medium">
              {user?.membership?.organizationName ?? 'No organization'}
            </span>
            <Icon name="chevronDown" size={11} className="text-faint" />
          </span>
        }
      >
        <MenuHeading>Organizations</MenuHeading>
        {(user?.memberships ?? []).map((membership) => (
          <MenuItem key={membership.organizationId} selected>
            <span className="min-w-0 flex-1 truncate">{membership.organizationName}</span>
            <span className="text-faint text-[10.5px]">
              {membership.role.toLowerCase().split('_').join(' ')}
            </span>
          </MenuItem>
        ))}
        <MenuNote>
          Switching organizations arrives with the SaaS surface. P027 deferred it deliberately —
          everyone has exactly one membership during the ICPK-only phase.
        </MenuNote>
      </Menu>

      <div className="bg-line mx-1 hidden h-4 w-px sm:block" />

      <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-1.5 sm:flex">
        <span className={cn('text-[12.5px]', crumb ? 'text-muted' : 'text-text')}>
          {TITLES[route]}
        </span>
        {crumb && (
          <>
            <Icon name="chevronRight" size={11} className="text-faint" />
            <span className="text-text max-w-[30ch] truncate font-mono text-[12px]">{crumb}</span>
          </>
        )}
      </nav>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* Global search opens the palette rather than filtering in place — it reaches across
            screens, and a box that sometimes filters and sometimes navigates is worse than either. */}
        <button
          onClick={onOpenPalette}
          className="bg-panel2 border-line text-faint hover:border-accent-line hidden w-[260px] items-center gap-[7px] rounded-[7px] border px-[9px] py-[5px] text-left text-[12px] transition-colors lg:flex"
        >
          <Icon name="search" size={12} />
          <span className="flex-1">Search everything</span>
          <kbd className="border-line text-faint rounded-[4px] border px-1 py-px font-sans text-[10px]">
            /
          </kbd>
        </button>

        <SendingChip
          known={known}
          sending={sending}
          stoppedByPlatform={stoppedByPlatform}
          loading={outbound.isLoading}
        />

        <Menu
          open={menu === 'notifications'}
          onToggle={() => setMenu(menu === 'notifications' ? null : 'notifications')}
          align="right"
          width={300}
          trigger={<Icon name="alerts" size={15} />}
          triggerLabel="Notifications"
          iconOnly
        >
          <MenuHeading>Notifications</MenuHeading>
          <MenuNote>
            Nothing sends yet, so nothing raises an alert. Bounce and complaint thresholds, domain
            degradation and warmup stalls all report here once the pipeline runs (§71).
          </MenuNote>
        </Menu>

        <Menu
          open={menu === 'help'}
          onToggle={() => setMenu(menu === 'help' ? null : 'help')}
          align="right"
          width={280}
          trigger={<Icon name="help" size={15} />}
          triggerLabel="Help and keyboard shortcuts"
          iconOnly
        >
          <MenuHeading>Keyboard</MenuHeading>
          <Shortcut keys="/" action="Search everything" />
          <Shortcut keys="Esc" action="Close a dialog, drawer or menu" />
          <Shortcut keys="Enter" action="Open the focused row" />
          <MenuNote>
            The console's own documentation lives in the repository, under `docs/`. Every screen
            names the architecture section it implements.
          </MenuNote>
        </Menu>

        <IconButton
          icon={theme === 'dark' ? 'sun' : 'moon'}
          label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          onClick={toggleTheme}
        />

        <IconButton
          icon="rows"
          label={density === 'dense' ? 'Comfortable rows' : 'Dense rows'}
          onClick={toggleDensity}
          className={density === 'dense' ? 'text-text' : undefined}
        />
      </div>
    </header>
  )
}

/** A popover menu. One implementation for all four in this bar. */
function Menu({
  open,
  onToggle,
  trigger,
  triggerLabel,
  iconOnly,
  align = 'left',
  width = 268,
  children,
}: {
  open: boolean
  onToggle: () => void
  trigger: React.ReactNode
  triggerLabel?: string
  iconOnly?: boolean
  align?: 'left' | 'right'
  width?: number
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onToggle])

  return (
    <div className="relative flex-none" ref={ref}>
      <button
        onClick={onToggle}
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-expanded={open}
        className={cn(
          'text-text flex items-center rounded-[7px] transition-colors',
          iconOnly
            ? 'border-line text-muted hover:bg-hover hover:text-text h-[30px] w-[30px] justify-center border'
            : 'hover:bg-hover border border-transparent px-2 py-[5px]',
        )}
      >
        {trigger}
      </button>

      {open && (
        <>
          <button aria-label="Close" tabIndex={-1} className="fixed inset-0 z-30" onClick={onToggle} />
          <div
            style={{ width }}
            className={cn(
              'bg-panel border-line animate-fade absolute top-[38px] z-40 rounded-[10px] border p-1.5',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {children}
          </div>
        </>
      )}
    </div>
  )
}

function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-faint px-[9px] pt-1 pb-1.5 text-[9.5px] tracking-[0.08em] uppercase">
      {children}
    </div>
  )
}

function MenuItem({ children, selected }: { children: React.ReactNode; selected?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-[6px] px-[9px] py-1.5 text-[12.5px]',
        selected ? 'bg-hover text-text' : 'text-muted',
      )}
    >
      {children}
    </div>
  )
}

function MenuNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted px-[9px] pt-1.5 pb-1 text-[11px] leading-[1.45] text-pretty">
      {children}
    </p>
  )
}

function Shortcut({ keys, action }: { keys: string; action: string }) {
  return (
    <div className="flex items-center gap-2 px-[9px] py-1 text-[12px]">
      <kbd className="border-line text-muted rounded-[4px] border px-1.5 py-px font-sans text-[10.5px]">
        {keys}
      </kbd>
      <span className="text-muted">{action}</span>
    </div>
  )
}

/**
 * Whether mail can leave.
 *
 * Three states, not two. Until the request lands the honest answer is "not known yet" — failing
 * closed is correct behaviour, but rendering a definite "stopped" while the request is in flight
 * tells an operator something that has not been read.
 *
 * The dot pulses only while sending. Motion means "something is happening right now", and a paused
 * system pulsing would be exactly the wrong signal.
 */
function SendingChip({
  known,
  sending,
  stoppedByPlatform,
  loading,
}: {
  known: boolean
  sending: boolean
  stoppedByPlatform: boolean
  loading: boolean
}) {
  if (!known) {
    return (
      <span className="border-line bg-panel2 text-faint flex items-center gap-[7px] rounded-[7px] border px-[9px] py-[5px] text-[12px]">
        <span className="bg-faint h-1.5 w-1.5 shrink-0 rounded-full" />
        {loading ? 'Checking…' : 'Cannot read'}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'flex items-center gap-[7px] rounded-[7px] border px-[9px] py-[5px] text-[12px]',
        sending ? 'border-line bg-okbg text-ok' : 'border-line bg-badbg text-bad',
      )}
      title={
        stoppedByPlatform
          ? 'Stopped at the platform level, for every organization'
          : sending
            ? 'Campaigns not in dry run can dispatch real email'
            : 'Nothing can send by any path'
      }
    >
      {/* Colour only. The design pulsed this dot with a box-shadow ring; there is no elevation
          anywhere in this product, and the word beside it already says the state. */}
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', sending ? 'bg-ok' : 'bg-bad')} />
      {sending ? 'Sending' : stoppedByPlatform ? 'Stopped by Axmion' : 'Sending stopped'}
    </span>
  )
}
